/**
 * Ordering Element Schema
 * Users arrange items into the correct sequence via drag-and-drop.
 * The correct order is the order defined in the editor.
 */

import type { ElementSchema, ListFieldSchema, TextFieldSchema } from '@ai-content-hub/content-kit';

export const defaultOrderingElementI18n = {
    en: {
        question: 'Question',
        enterQuestionInstruction: 'Enter question instruction...',
        addItem: 'Add item',
        moveUp: 'Move up',
        moveDown: 'Move down',
        removeItem: 'Remove item',
        explanationAfterSubmit: 'Explanation (after submit)',
        explainCorrectAnswer: 'Explain the correct answer...',
        orderingPlayerNoData: 'No ordering data',
        dragToReorder: 'Drag to reorder',
        explanation: 'Explanation',
    },
    vi: {
        question: 'Câu hỏi',
        enterQuestionInstruction: 'Nhập hướng dẫn câu hỏi...',
        addItem: 'Thêm mục',
        moveUp: 'Di chuyển lên',
        moveDown: 'Di chuyển xuống',
        removeItem: 'Xóa mục',
        explanationAfterSubmit: 'Giải thích (sau khi nộp)',
        explainCorrectAnswer: 'Giải thích đáp án đúng...',
        orderingPlayerNoData: 'Không có dữ liệu sắp xếp',
        dragToReorder: 'Kéo để sắp xếp',
        explanation: 'Giải thích',
    },
};

/**
 * A single ordering item
 */
export interface OrderingItem {
    id: string;
    text: string;
}

/**
 * Ordering Element Data
 */
export interface OrderingElementData {
    question: string;
    items: OrderingItem[];
    explanation?: string;
}

/**
 * Answer format: ordered array of item ids representing the user's sequence
 */
export type OrderingAnswer = string[];

/**
 * Create a unique id for a new item
 */
function uid(): string {
    return `ord-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Create a default ordering item
 */
export function createDefaultOrderingItem(index: number): OrderingItem {
    return {
        id: uid(),
        text: `Item ${index + 1}`,
    };
}

/**
 * Normalize ordering data – ensures all required fields exist
 */
export function normalizeOrderingData(data: Partial<OrderingElementData>): OrderingElementData {
    const items: OrderingItem[] = (data.items || []).map((item, index) => ({
        id: item.id || uid(),
        text: item.text ?? `Item ${index + 1}`,
    }));
    return {
        question: data.question ?? '',
        items: items.length >= 2 ? items : [createDefaultOrderingItem(0), createDefaultOrderingItem(1)],
        explanation: data.explanation ?? '',
    };
}

/**
 * Get the correct order (the editor order)
 */
export function getCorrectOrder(data: OrderingElementData): string[] {
    return data.items.map((item) => item.id);
}

export const MIN_ITEMS = 2;
export const MAX_ITEMS = 10;

export const orderingElementSchema: ElementSchema<OrderingElementData> = {
    type: 'element-ordering',
    title: 'Ordering',
    description: 'Arrange items in the correct sequence by dragging and dropping.',
    version: '1.0.0',
    category: 'question',
    icon: 'ListOrdered',
    gradable: true,
    tags: ['ordering', 'sequence', 'question', 'assessment', 'drag-drop'],
    fields: [
        {
            name: 'question',
            type: 'text',
            label: 'Question',
            description: 'The question or instruction for ordering',
            required: true,
        } as TextFieldSchema,
        {
            name: 'items',
            type: 'list',
            label: 'Items',
            description: 'Items to be ordered. The order here is the correct answer.',
            required: true,
            minItems: MIN_ITEMS,
            maxItems: MAX_ITEMS,
        } as ListFieldSchema,
        {
            name: 'explanation',
            type: 'text',
            label: 'Explanation',
            description: 'Explanation shown after answer',
            placeholder: 'Explain the correct answer...',
            multiline: true,
        } as TextFieldSchema,
    ],
    defaultData: {
        question: '',
        items: [
            createDefaultOrderingItem(0),
            createDefaultOrderingItem(1),
            createDefaultOrderingItem(2),
            createDefaultOrderingItem(3),
        ],
        explanation: '',
    },
    i18n: defaultOrderingElementI18n,
};
